import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Avatar,
    IconButton,
    Pagination,
    Paper,
    Divider,
    CircularProgress,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
    TimelineDot,
    TimelineConnector,
    TimelineContent
} from '@mui/lab';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Share as ShareIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    History as HistoryIcon,    
    NotificationsActive as AlertIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    SwapHoriz as TransferIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { utcDisplay } from '../../utilis/date';
import * as alertAuditService from '../../services/alert-audit-log.service';
import ReportHistoryDisplay from './ReportHistoryDisplay';

const getActionIcon = (action) => {
    const iconProps = { fontSize: 'small' };
    switch (action) {
        case 'CREATED': return <AddIcon {...iconProps} />;
        case 'CUSTOM_ALERT_CREATED': return <AddIcon {...iconProps} />;
        case 'UPDATED': 
        case 'NAME_CHANGED':
        case 'INTERVAL_CHANGED':
        case 'FACILITIES_CHANGED': return <EditIcon {...iconProps} />;
        case 'DELETED': 
        case 'CUSTOM_ALERT_DELETED': return <DeleteIcon {...iconProps} />;
        case 'SHARED': return <ShareIcon {...iconProps} />;
        case 'CUSTOM_ALERT_ADDED': return <AlertIcon {...iconProps} />;
        case 'DEFAULT_ALERT_FACILITY_TOGGLED': return <EditIcon {...iconProps} />;
        case 'CUSTOM_ALERT_UPDATED': return <EditIcon {...iconProps} />;
        case 'ACTIVATED': 
        case 'DEACTIVATED':
        case 'CUSTOM_ALERT_TOGGLED':
        case 'FACILITY_TOGGLED':
        case 'STATUS_TOGGLED': return <ScheduleIcon {...iconProps} />;
        default: return <HistoryIcon {...iconProps} />;
    }
};

const getActionColor = (action) => {
    switch (action) {
        case 'CREATED': 
        case 'CUSTOM_ALERT_ADDED': return 'success';
        case 'UPDATED':
        case 'NAME_CHANGED':
        case 'INTERVAL_CHANGED':
        case 'FACILITIES_CHANGED':
        case 'CUSTOM_ALERT_UPDATED': return 'primary';
        case 'DELETED': 
        case 'CUSTOM_ALERT_DELETED': return 'error';
        case 'SHARED': return 'info';
        case 'ACTIVATED': return 'success';
        case 'DEACTIVATED': return 'warning';
        case 'STATUS_TOGGLED': return 'primary';
        case 'FACILITY_TOGGLED': return 'primary';
        case 'CUSTOM_ALERT_CREATED': return 'success';
        case 'CUSTOM_ALERT_TOGGLED': return 'primary';
        case 'DEFAULT_ALERT_FACILITY_TOGGLED': return 'primary';
        default: return 'default';
    }
};

const AlertHistoryDialog = ({ open, onClose, alertReportId, alertName, customAlertSlug }) => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);

    useEffect(() => {
        if (open && alertReportId) {
            fetchAuditLogs();
            fetchAuditSummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, alertReportId, page]);

    const fetchAuditLogs = async () => {
        try {
            
            setLoading(true);
            const response = await alertAuditService.getAlertAuditHistory(alertReportId, page, 20, customAlertSlug);
            
            if (response) {
                // The API interceptor returns response.data directly
                setAuditLogs(response?.logs || []);
                setTotalPages(response?.totalPages || 1);
                setTotalLogs(response?.total || 0);
            }
        } catch (error) {
            console.error('Error fetching alert audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditSummary = async () => {
        try {
            setSummaryLoading(true);
            const response = await alertAuditService.getAlertAuditSummary(alertReportId, customAlertSlug);
            
            if (response) {
                // The API interceptor returns response.data directly
                setSummary(response);
            }
        } catch (error) {
            console.error('Error fetching alert audit summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const formatDate = (date) => {
        if (!date) return '';
        return utcDisplay(date, 'MMM DD, YYYY • HH:mm');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '70vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AlertIcon color="primary" />
                    <Box>
                        <Typography variant="h6" component="div">
                            Alert History
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {alertName || 'Alert Report'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                {/* Summary Section */}
                {summaryLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : summary && (
                    <Box sx={{ px: 3, py: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: 16 }} />
                                            Creator Information
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: 14, backgroundColor: '#1976d2' }}>
                                                {summary.creator?.fullName?.[0] || 'U'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {summary.creator?.fullName || summary.creator?.name || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Created {summary.createdAt ? formatDate(summary.createdAt) : 'Unknown'}
                                                </Typography>
                                            </Box>
                                        </Box>                                       
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <InfoIcon sx={{ fontSize: 16 }} />
                                            Alert Details
                                        </Typography>
                                        
                                        {/* Alert Description or Type Array fallback */}
                                        {(() => {
                                            // Find the first custom alert with a description
                                            const customAlertWithDescription = summary.alertReport?.customAlert?.find(alert => alert.description);
                                            
                                            if (customAlertWithDescription?.description) {
                                                return (
                                                    <ReportHistoryDisplay 
                                                        description={customAlertWithDescription.description}
                                                        isActive={summary.alertReport?.isActive}
                                                        type="ALERT"
                                                        alertReport={summary?.alertReport}
                                                    />
                                                );
                                            } else if (summary.alertReport?.type) {
                                                return (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                            Alert Frequency ({summary.alertReport.type.length})
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                                            {summary.alertReport.type.map((type, index) => (
                                                                <Chip 
                                                                    key={index}
                                                                    size="small" 
                                                                    label={type}
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{ fontSize: '0.7rem' }}
                                                                />
                                                            ))}
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                            Page: <strong>{summary.alertReport.page}</strong>
                                                        </Typography>
                                                        {summary.alertReport.facilityName && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                Facility: <strong>{summary.alertReport.facilityName}</strong>
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            } else {
                                                return (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No alert configuration details available
                                                    </Typography>
                                                );
                                            }
                                        })()}

                                        {/* Boolean Flags */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TrendingUpIcon 
                                                    sx={{ 
                                                        fontSize: 16, 
                                                        color: summary.alertReport?.isRising ? 'success.main' : 'text.disabled' 
                                                    }} 
                                                />
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: summary.alertReport?.isRising ? 'success.main' : 'text.disabled',
                                                        fontWeight: summary.alertReport?.isRising ? 600 : 400
                                                    }}
                                                >
                                                    Rising: {summary.alertReport?.isRising ? 'True' : 'False'}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TrendingDownIcon 
                                                    sx={{ 
                                                        fontSize: 16, 
                                                        color: summary.alertReport?.isDropping ? 'error.main' : 'text.disabled' 
                                                    }} 
                                                />
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: summary.alertReport?.isDropping ? 'error.main' : 'text.disabled',
                                                        fontWeight: summary.alertReport?.isDropping ? 600 : 400
                                                    }}
                                                >
                                                    Dropping: {summary.alertReport?.isDropping ? 'True' : 'False'}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TransferIcon 
                                                    sx={{ 
                                                        fontSize: 16, 
                                                        color: summary.alertReport?.isTransferNeedWork ? 'warning.main' : 'text.disabled' 
                                                    }} 
                                                />                                                
                                            </Box>
                                        </Box>                                        
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <Divider />

                {/* History Timeline */}
                <Box sx={{ px: 3, py: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : auditLogs.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No audit history found for this alert report.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total activities: {totalLogs}
                                </Typography>
                                <Chip 
                                    label={`Page ${page} of ${totalPages}`} 
                                    size="small" 
                                    variant="outlined" 
                                />
                            </Box>
                            
                            <Timeline>
                                {auditLogs.map((log, index) => (
                                    <TimelineItem key={log._id}>
                                        <TimelineOppositeContent sx={{ flex: 0.3, pr: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(log.timestamp)}
                                            </Typography>
                                            {log.targetUser && (
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip 
                                                        size="small" 
                                                        label={log.targetUser.fullName}
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                </Box>
                                            )}
                                        </TimelineOppositeContent>
                                        
                                        <TimelineSeparator>
                                            <TimelineDot color={getActionColor(log.action)} variant="outlined">
                                                {getActionIcon(log.action)}
                                            </TimelineDot>
                                            {index < auditLogs.length - 1 && <TimelineConnector />}
                                        </TimelineSeparator>
                                        
                                        <TimelineContent sx={{ flex: 1 }}>
                                            <Paper 
                                                elevation={1} 
                                                sx={{ 
                                                    p: 2, 
                                                    border: '1px solid #e0e0e0',
                                                    '&:hover': {
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                                            {log.description || log.actionDescription}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, backgroundColor: '#1976d2' }}>
                                                                {log.user?.name?.[0] || log.user?.fullName?.[0] || <PersonIcon sx={{ fontSize: 12 }} />}
                                                            </Avatar>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {log.user?.name || log.user?.fullName || 'Unknown User'}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        {/* Only show details if there are meaningful details beyond just the description */}
                                                        {log.details && Object.keys(log.details).length > 0 && !(Object.keys(log.details).length === 1 && log.details.description) && (
                                                            <Box
                                                                sx={{
                                                                    mt: 1,
                                                                    p: 1.5,
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: 1,
                                                                    border: '1px solid #e9ecef'
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                    Additional Details:
                                                                </Typography>
                                                                {Object.entries(log.details).filter(([key]) => key !== 'description').map(([key, value]) => (
                                                                    <Typography key={key} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                                        <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )}

                                                        {log.ipAddress && (
                                                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                                                                IP: {log.ipAddress}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Chip 
                                                        size="small" 
                                                        label={log.action}
                                                        color={getActionColor(log.action)}
                                                        variant="outlined"
                                                        sx={{ ml: 1, fontSize: '0.7rem', fontWeight: 500 }}
                                                    />
                                                </Box>
                                            </Paper>
                                        </TimelineContent>
                                    </TimelineItem>
                                ))}
                            </Timeline>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        variant="outlined"
                                        shape="rounded"
                                        size="medium"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AlertHistoryDialog; 